import { z } from "@brains/utils/zod";
import {
  guestExecutionLimitsSchema,
  guestRetentionSchema,
} from "@brains/contracts/chat";

const positiveInteger = z.number().int().positive();
const positiveAmount = z.number().positive();

type Strict<Shape extends z.ZodRawShape> = z.ZodObject<Shape, z.core.$strict>;

export const guestIssuanceLimitsSchema: Strict<{
  requestsPerMinute: z.ZodNumber;
  requestsPerDay: z.ZodNumber;
  maxStoredCredentials: z.ZodNumber;
}> = z
  .strictObject({
    requestsPerMinute: positiveInteger,
    requestsPerDay: positiveInteger,
    maxStoredCredentials: positiveInteger,
  })
  .refine(
    (limits) => limits.requestsPerMinute <= limits.requestsPerDay,
    "Guest issuance minute limit must fit the daily limit",
  );
export type GuestIssuanceLimits = z.output<typeof guestIssuanceLimitsSchema>;

const limitsSchema: Strict<
  typeof guestExecutionLimitsSchema.shape & {
    userTurns: z.ZodNumber;
    requestsPerMinute: z.ZodNumber;
    requestsPerDay: z.ZodNumber;
    globalRequestsPerMinute: z.ZodNumber;
    globalRequestsPerDay: z.ZodNumber;
    globalConcurrency: z.ZodNumber;
  }
> = z
  .strictObject({
    ...guestExecutionLimitsSchema.shape,
    userTurns: positiveInteger,
    requestsPerMinute: positiveInteger,
    requestsPerDay: positiveInteger,
    globalRequestsPerMinute: positiveInteger,
    globalRequestsPerDay: positiveInteger,
    globalConcurrency: positiveInteger,
  })
  .refine(
    (limits) =>
      limits.requestsPerMinute <= limits.requestsPerDay &&
      limits.globalRequestsPerMinute <= limits.globalRequestsPerDay,
    "Guest rate limits must fit their daily limits",
  );

const budgetSchema: Strict<{
  dailyUsd: z.ZodNumber;
  maxTurnUsd: z.ZodNumber;
}> = z
  .strictObject({
    dailyUsd: positiveAmount,
    maxTurnUsd: positiveAmount,
  })
  .refine(
    (budget) => budget.maxTurnUsd <= budget.dailyUsd,
    "Guest turn reservation must fit within the daily budget",
  );

/** Exact deployment origin; forwarded headers and browser claims cannot supply it. */
export function isGuestOrigin(value: string): boolean {
  try {
    const url = new URL(value);
    if (url.origin !== value || url.username || url.password) return false;
    return (
      url.protocol === "https:" ||
      (url.protocol === "http:" &&
        ["localhost", "127.0.0.1", "[::1]"].includes(url.hostname))
    );
  } catch {
    // A malformed deployment URL is a policy validation failure, not an origin.
    return false;
  }
}

/** Finite bounds of the owner's usage record; set before anything is recorded. */
export const guestUsageBoundsSchema: Strict<{
  maxRecords: z.ZodNumber;
  maxDenialRecords: z.ZodNumber;
  retentionSeconds: z.ZodNumber;
  questionBytes: z.ZodNumber;
  maxStoredBytes: z.ZodNumber;
}> = z.strictObject({
  maxRecords: positiveInteger.max(10_000),
  /** Detailed denials kept; beyond this, only daily counts by reason. */
  maxDenialRecords: positiveInteger.max(10_000),
  retentionSeconds: positiveInteger.max(366 * 86_400),
  /** UTF-8 bytes of a question kept; longer questions are cut and marked. */
  questionBytes: positiveInteger.max(65_536),
  /** Total question text kept; a request that could exceed it is refused. */
  maxStoredBytes: positiveInteger.max(64 * 1024 * 1024),
});
export type GuestUsageBounds = z.output<typeof guestUsageBoundsSchema>;

const disabledPolicySchema: Strict<{ enabled: z.ZodLiteral<false> }> =
  z.strictObject({ enabled: z.literal(false) });
const enabledPolicySchema: Strict<{
  enabled: z.ZodLiteral<true>;
  origin: z.ZodString;
  previewOrigin: z.ZodOptional<z.ZodString>;
  budgeted: z.ZodOptional<z.ZodLiteral<true>>;
  issuance: typeof guestIssuanceLimitsSchema;
  limits: typeof limitsSchema;
  retention: typeof guestRetentionSchema;
  budget: typeof budgetSchema;
  usageRecord: typeof guestUsageBoundsSchema;
  disclosure: Strict<{
    provider: z.ZodString;
    notice: z.ZodString;
    deletionLimitations: z.ZodString;
  }>;
}> = z.strictObject({
  enabled: z.literal(true),
  origin: z
    .string()
    .refine(
      isGuestOrigin,
      "Guest origin must be canonical HTTPS (or loopback HTTP)",
    ),
  /** The site's preview, served under the same switch, budget and limits. */
  previewOrigin: z
    .string()
    .refine(
      (value) => isGuestOrigin(value) && value.startsWith("https://"),
      "Guest preview origin must be canonical HTTPS",
    )
    .optional(),
  /** The owner authorizes it with a monthly budget; admission needs that authorization. */
  budgeted: z.literal(true).optional(),
  issuance: guestIssuanceLimitsSchema,
  limits: limitsSchema,
  retention: guestRetentionSchema,
  budget: budgetSchema,
  usageRecord: guestUsageBoundsSchema,
  disclosure: z.strictObject({
    provider: z.string().trim().min(1),
    notice: z.string().trim().min(1),
    deletionLimitations: z.string().trim().min(1),
  }),
});

// No proposed launch limits become implicit defaults. Configuration alone is
// not admission: runtime isolation and atomic quota reservations remain gates.
export const guestPolicySchema: z.ZodDiscriminatedUnion<
  [typeof disabledPolicySchema, typeof enabledPolicySchema],
  "enabled"
> = z.discriminatedUnion("enabled", [
  disabledPolicySchema,
  enabledPolicySchema,
]);
export type GuestPolicy = z.output<typeof guestPolicySchema>;
export type EnabledGuestPolicy = Extract<GuestPolicy, { enabled: true }>;

/** Deployment TLS terminates at the HTTPS proxy. For bounded guest access,
 * accept its backend HTTP scheme only for an exact configured HTTPS host.
 * Never infer the public host or protocol from Forwarded/X-Forwarded-* claims.
 * Loopback policies retain exact-origin and socket-peer checks.
 * Returns the configured origin the request arrived on, if any.
 */
export function guestRequestOrigin(
  request: Request,
  policy: EnabledGuestPolicy,
): string | undefined {
  const url = new URL(request.url);
  const origins = [policy.origin, policy.previewOrigin].filter(
    (origin): origin is string => origin !== undefined,
  );
  return origins.find(
    (origin) =>
      url.origin === origin ||
      (policy.budgeted === true &&
        new URL(origin).protocol === "https:" &&
        url.protocol === "http:" &&
        url.host === new URL(origin).host),
  );
}

export function matchesGuestOrigin(
  request: Request,
  policy: EnabledGuestPolicy,
): boolean {
  return guestRequestOrigin(request, policy) !== undefined;
}
